const asList = (value) => (Array.isArray(value) ? value : []);

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const normalizedText = (value) => String(value || '')
  .normalize('NFKC')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const AI_PROOF_PATTERN = /\b(?:ai|llm|artificial intelligence|machine learning|openai|anthropic|gpt(?:-\d+(?:\.\d+)?)?|claude|gemini|llama|mistral|cohere)\b/i;

// An AI badge is a provenance statement, so a generic "classified" flag is
// deliberately insufficient. Only explicit backend method/model metadata can
// prove that an AI classification actually ran.
export const aiClassificationProof = (record) => {
  if (!record || typeof record !== 'object') return null;
  const candidates = [
    record.hazard_gate,
    record.processing_contract,
    record.processing,
    record.classification_processing,
    record.classification_metadata,
    record.ai_processing,
    record,
  ].filter((candidate) => candidate && typeof candidate === 'object');
  for (const candidate of candidates) {
    const method = firstText(
      candidate.method,
      candidate.processing_method,
      candidate.classification_method,
      candidate.classifier
    );
    const model = firstText(
      candidate.model,
      candidate.model_name,
      candidate.ai_model,
      candidate.classification_model
    );
    const proofText = `${method} ${model}`;
    if (AI_PROOF_PATTERN.test(proofText) || AI_PROOF_PATTERN.test(normalizedText(proofText))) {
      return { method, model };
    }
  }
  return null;
};

const sourceIdentityValues = (source) => [
  source?.external_id,
  source?.intel_id,
  source?.evidence_id,
  source?.connection_id,
  source?.origin_key,
  source?.source_url,
  source?.article_url,
  source?.canonical_url,
  source?.url,
  source?.publisher_name,
  source?.origin_name,
  source?.publisher,
  source?.source_name,
  source?.display_name,
  source?.name,
].map(normalizedText).filter(Boolean);

const strongSourceIdentityValues = (source) => [
  source?.external_id,
  source?.intel_id,
  source?.evidence_id,
  source?.connection_id,
  source?.source_url,
  source?.article_url,
  source?.canonical_url,
  source?.url,
].map(normalizedText).filter(Boolean);

export const sourceAiClassificationProof = (source, record) => {
  const directProof = aiClassificationProof(source);
  if (directProof) return directProof;
  const recordProof = aiClassificationProof(record);
  if (!recordProof) return null;
  const detector = record?.detecting_source || record?.connection || record?.source;
  const detectorStrongValues = new Set([
    ...strongSourceIdentityValues(detector),
    normalizedText(record?.external_id),
    normalizedText(record?.source_url),
  ].filter(Boolean));
  const sourceStrongValues = strongSourceIdentityValues(source);
  if (detectorStrongValues.size) {
    return sourceStrongValues.some((value) => detectorStrongValues.has(value))
      ? recordProof
      : null;
  }
  const detectorNameValues = new Set(sourceIdentityValues(detector));
  const recordSourceName = normalizedText(record?.source_name);
  if (recordSourceName) detectorNameValues.add(recordSourceName);
  return sourceIdentityValues(source).some((value) => detectorNameValues.has(value))
    ? recordProof
    : null;
};

const readableValue = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return firstText(value?.label, value?.display_name, value?.name, value?.text);
};

export const humanizeClaim = (claim) => {
  if (typeof claim === 'string') {
    const value = claim.trim();
    return value && !/[.!?]$/.test(value) ? `${value}.` : value;
  }
  if (!claim || typeof claim !== 'object') return '';
  const direct = firstText(
    claim.statement,
    claim.text,
    claim.claim,
    claim.description,
    claim.title
  );
  if (direct) {
    const value = direct.replace(/\s+/g, ' ').trim();
    const sentence = `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
  }
  const subject = readableValue(claim.subject);
  const predicateKey = readableValue(claim.predicate).toLowerCase().trim();
  const predicate = predicateKey.replaceAll('_', ' ').trim();
  const rawValue = claim.value;
  const value = typeof rawValue === 'boolean' ? '' : readableValue(rawValue);
  let templated = '';
  if (predicateKey === 'targeted_by') {
    templated = [subject, value && `was reported as targeted by ${value}`].filter(Boolean).join(' ');
  } else if (predicateKey === 'projectile_impact') {
    templated = subject
      ? `A projectile impact was reported for ${subject}`
      : 'A projectile impact was reported';
  } else if (predicateKey === 'fire_on_board') {
    templated = subject
      ? `A fire on board ${subject} was reported`
      : 'A fire on board was reported';
  } else if (predicateKey === 'crew_safe') {
    templated = subject
      ? `The crew of ${subject} was reported safe`
      : 'The crew was reported safe';
  } else if (predicateKey === 'attack_reported') {
    templated = subject
      ? `An attack involving ${subject} was reported`
      : 'An attack was reported';
  } else if (
    rawValue === true &&
    (/_event_(?:occurred|reported)$/.test(predicateKey) || predicateKey === 'maritime_incident_reported')
  ) {
    // For normalized occurrence claims the subject is the source's readable
    // headline. Repeating the technical predicate adds no information and can
    // misleadingly look like platform confirmation.
    templated = subject || 'An event was reported';
  }
  const structured = [
    subject,
    predicate,
    value,
  ].filter(Boolean).join(' ');
  const readable = (templated || structured).replace(/\s+/g, ' ').trim();
  if (!readable) return '';
  const sentence = `${readable.charAt(0).toUpperCase()}${readable.slice(1)}`;
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

export const humanizeFact = (fact) => {
  if (typeof fact === 'string') return humanizeClaim(fact);
  if (!fact || typeof fact !== 'object') return '';
  const direct = firstText(
    fact.statement,
    fact.text,
    fact.fact,
    fact.description
  );
  if (direct) return humanizeClaim(direct);

  const subject = readableValue(fact.subject);
  const predicateKey = readableValue(fact.predicate).toLowerCase().trim();
  const rawValue = fact.value;
  const value = typeof rawValue === 'boolean' ? '' : readableValue(rawValue);

  if (
    ['source_published_report', 'publication_observed'].includes(predicateKey) ||
    fact?.fact_type === 'publication_observed'
  ) {
    return humanizeClaim(value || `${subject} published a source report`);
  }
  if (
    rawValue === true &&
    (/_event_(?:occurred|reported)$/.test(predicateKey) || /_incident_reported$/.test(predicateKey))
  ) {
    return humanizeClaim(subject || 'An event was reported');
  }
  return humanizeClaim({
    subject,
    predicate: predicateKey,
    value,
  });
};

export const claimSourceLabel = (claim) => {
  if (!claim || typeof claim !== 'object') return '';
  const direct = firstText(
    claim.asserted_by,
    claim.actor,
    claim.source_name,
    claim.publisher_name,
    claim.origin_name,
    claim.publisher
  );
  if (direct) return direct;
  const evidence = asList(claim.evidence);
  return evidence.length ? evidencePublisher(evidence[0]) : '';
};

export const claimStatusLabel = (claim) => {
  const status = normalizedText(firstText(
    claim?.verification_lifecycle?.status,
    claim?.truth_status,
    claim?.verification_status,
    claim?.epistemic_status,
    claim?.status
  ));
  if (/\b(?:retracted|withdrawn|removed)\b/.test(status)) return 'Retracted';
  if (/\b(?:disputed|contradicted|contested|refuted)\b/.test(status)) return 'Disputed';
  if (
    /\b(?:confirmed|verified)\b/.test(status) &&
    !/\b(?:unconfirmed|not (?:independently )?(?:confirmed|verified)|requires (?:verification|assessment))\b/.test(status)
  ) {
    return 'Confirmed';
  }
  if (/\b(?:corroborated|independently corroborated|multiple sources)\b/.test(status)) {
    return 'Corroborated';
  }
  return 'Reported';
};

export const dedupeClaims = (claims) => {
  const seen = new Set();
  return asList(claims).filter((claim) => {
    const sentence = humanizeClaim(claim);
    if (!sentence) return false;
    const semanticKey = firstText(
      claim?.verification_lifecycle?.semantic_key,
      claim?.semantic_key
    );
    const structured = [
      readableValue(claim?.subject),
      readableValue(claim?.predicate),
      readableValue(claim?.value),
    ].filter(Boolean).join(' ');
    const key = normalizedText(semanticKey || structured || sentence);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const evidenceTimestamp = (item) => firstText(
  item?.source_published_at,
  item?.published_at,
  item?.occurred_at,
  item?.platform_detected_at,
  item?.detected_at,
  item?.updated_at,
  item?.created_at
);

// Update streams include the original detector snapshot in some backend
// records. Always remove that exact baseline and order only later merged
// observations newest first.
export const collectLatestUpdates = (record) => {
  const raw = [
    ...asList(record?.dedupe?.observation_timeline),
    ...asList(record?.dedupe?.updates),
    ...asList(record?.observation_timeline),
    ...asList(record?.updates),
  ].filter((item) => item && typeof item === 'object');
  const byKey = new Map();
  raw.forEach((item, index) => {
    const key = [
      firstText(item?.update_key, item?.external_id, item?.id),
      safeEvidenceUrl(item),
      evidenceTimestamp(item),
      evidenceTitle(item),
    ].join('|') || String(index);
    if (!byKey.has(key)) byKey.set(key, item);
  });
  let updates = [...byKey.values()];
  const detector = record?.detecting_source || record?.connection || record?.source;
  const detectorIds = new Set([
    ...strongSourceIdentityValues(detector),
    normalizedText(record?.external_id),
    normalizedText(record?.source_url),
  ].filter(Boolean));
  const detectorIdentity = new Set([
    ...sourceIdentityValues(detector),
    normalizedText(record?.source_name),
  ].filter(Boolean));
  const baselineTitle = normalizedText(firstText(
    record?.source_title,
    record?.original_title,
    detector?.source_title,
    record?.title
  ));
  const baselineTimestamp = Date.parse(firstText(
    record?.source_published_at,
    record?.timing?.published_at,
    record?.occurred_at,
    record?.event_occurred_at,
    detector?.source_published_at,
    record?.timing?.first_seen_at
  )) || 0;
  updates = updates.filter((item) => {
    const identityMatches = (
      strongSourceIdentityValues(item).some((value) => detectorIds.has(value)) ||
      sourceIdentityValues(item).some((value) => detectorIdentity.has(value))
    );
    if (!identityMatches) return true;
    const itemTitle = normalizedText(evidenceTitle(item, ''));
    const itemTimestamp = Date.parse(evidenceTimestamp(item)) || 0;
    const titleMatches = !baselineTitle || !itemTitle || itemTitle === baselineTitle;
    const timeMatches = !baselineTimestamp || !itemTimestamp || itemTimestamp === baselineTimestamp;
    return !(titleMatches && timeMatches);
  });
  return updates.sort((left, right) => {
    const leftTime = Date.parse(evidenceTimestamp(left)) || 0;
    const rightTime = Date.parse(evidenceTimestamp(right)) || 0;
    return rightTime - leftTime;
  });
};

export const safeEvidenceUrl = (item) => {
  const value = firstText(
    item?.source_url,
    item?.article_url,
    item?.canonical_url,
    item?.url,
    item?.link
  );
  if (!value) return '';
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
  } catch (error) {
    return '';
  }
};

export const evidenceTitle = (item, fallback = 'Source report') => firstText(
  item?.source_title,
  item?.original_title,
  item?.title,
  item?.headline,
  item?.article_title,
  item?.report_title,
  item?.text,
  fallback
);

export const evidencePublisher = (item) => firstText(
  item?.publisher_name,
  item?.origin_name,
  item?.publisher,
  item?.source_name,
  item?.display_name,
  item?.name,
  item?.domain,
  item?.origin_key,
  'Unknown publisher'
);

export const evidenceSourceType = (item) => firstText(
  item?.source_type,
  item?.origin_type,
  item?.publisher_type,
  item?.category
);

export const evidenceVerification = (item) => firstText(
  item?.verification_status,
  item?.verification?.status,
  item?.fact_check_status
);

export const evidenceStance = (item) => firstText(
  item?.verification_stance,
  item?.stance,
  item?.claim_stance,
  item?.evidence_role,
  item?.relation
);

export const evidencePublishedAt = (item) => firstText(
  item?.published_at,
  item?.source_published_at,
  item?.occurred_at,
  item?.created_at
);

const nestedEvidence = (item) => [
  ...asList(item?.all_matches),
  ...asList(item?.cluster_members),
  ...asList(item?.cluster_reports),
  ...asList(item?.reports),
  ...asList(item?.items),
];

// The server's canonical `corroboration.all_matches` contains the complete
// evidence set. `matches` remains supported only for older warning records.
// Nested cluster members are expanded so the UI never hides exact reports
// behind a "+N" summary.
export const collectEvidence = (record) => {
  const corroboration = record?.corroboration || {};
  const corroborationItems = Array.isArray(corroboration.all_matches)
    ? corroboration.all_matches
    : asList(corroboration.matches);
  const detectingSource = record?.detecting_source || record?.connection || record?.source;
  const sourceRecord = asList(record?.sources).find((source) =>
    source?.is_detector === true ||
    source?.is_detecting_source === true ||
    source?.is_primary_detection === true ||
    ['detector', 'detection', 'origin'].includes(String(source?.case_role || source?.role || '').toLowerCase())
  ) || asList(record?.sources)[0];
  const detector = detectingSource && typeof detectingSource === 'object'
    ? detectingSource
    : (typeof detectingSource === 'string' ? { name: detectingSource } : sourceRecord);
  const primary = record?.primary_evidence || record?.primary_observation || record?.detection_evidence ||
    (detector ? {
      ...sourceRecord,
      ...detector,
      external_id: detector?.external_id || record?.external_id,
      title: detector?.title || record?.original_title || record?.title,
      source_url: detector?.source_url || sourceRecord?.source_url || record?.source_url,
      evidence_role: detector?.evidence_role || 'detection',
    } : null);
  const queue = [
    ...(primary ? [primary] : []),
    ...asList(record?.evidence),
    ...asList(record?.evidence_items),
    ...corroborationItems,
  ];
  const expanded = [];
  const visit = (item) => {
    if (!item || typeof item !== 'object') return;
    expanded.push(item);
    nestedEvidence(item).forEach(visit);
  };
  queue.forEach(visit);

  const deduped = [];
  const exactPairIndex = new Map();
  expanded.forEach((item) => {
    const url = safeEvidenceUrl(item).toLowerCase();
    // A detector promoted into a warning and the matching evidence-ledger row
    // can have different internal ids while retaining the same provider event
    // id. Prefer that provider id so the exact source report is rendered once.
    const id = firstText(item?.external_id, item?.intel_id, item?.evidence_id, item?.id);
    // Separate observations may legitimately link to the same article or
    // reuse a provider id. Collapse only a truly identical (id, URL) pair.
    if (!id || !url) {
      deduped.push(item);
      return;
    }
    const key = `${id.toLowerCase()}|${url}`;
    if (exactPairIndex.has(key)) {
      const index = exactPairIndex.get(key);
      deduped[index] = { ...deduped[index], ...item };
      return;
    }
    exactPairIndex.set(key, deduped.length);
    deduped.push(item);
  });
  return deduped;
};

export const evidenceVerificationLabel = (item) => {
  const value = evidenceVerification(item).replaceAll('_', ' ').trim();
  if (value) return value;
  if (item?.verification_required === true || item?.requires_verification === true) {
    return 'Requires verification';
  }
  return '';
};

const factObservationKind = (fact) => {
  const predicate = normalizedText(fact?.predicate);
  const kind = normalizedText(firstText(fact?.kind, fact?.type));
  const lifecycle = fact?.verification_lifecycle || {};
  if (
    ['source published report', 'publication observed'].includes(predicate) ||
    fact?.fact_type === 'publication_observed' ||
    lifecycle.scope === 'publication_only' ||
    lifecycle.basis === 'publication_observed' ||
    ['publication', 'published report', 'source observation'].includes(kind)
  ) return 'publication';
  if (
    fact?.confirmation_basis === 'publisher_observed_statement' ||
    lifecycle.scope === 'statement_was_made_only' ||
    lifecycle.basis === 'speech_act_observed'
  ) return 'statement';
  return '';
};

const factActivityStatus = (fact) => {
  const observationKind = factObservationKind(fact);
  if (observationKind === 'publication') return 'Publication observed';
  if (observationKind === 'statement') return 'Statement observed';
  const status = normalizedText(firstText(
    fact?.verification_status,
    fact?.truth_status,
    fact?.epistemic_status,
    fact?.status
  ));
  if (
    /\b(?:confirmed|verified)\b/.test(status) &&
    !/\b(?:unconfirmed|not (?:independently )?(?:confirmed|verified)|requires verification)\b/.test(status)
  ) return 'Verified';
  return 'Observation';
};

const evidenceActivityStatus = (item) => {
  const verification = normalizedText(evidenceVerification(item));
  if (
    /\b(?:verified|confirmed)\b/.test(verification) &&
    !/\b(?:unconfirmed|not (?:independently )?(?:confirmed|verified)|requires verification)\b/.test(verification)
  ) return 'Verified';
  if (item?.verification_required === true || item?.requires_verification === true ||
      /\b(?:requires verification|unverified|unconfirmed)\b/.test(verification)) {
    return 'Needs verification';
  }
  return 'Evidence';
};

const ACTIVITY_STATUS_RANK = new Map([
  ['Verified', 6],
  ['Publication observed', 5],
  ['Statement observed', 4],
  ['Update', 3],
  ['Evidence', 2],
  ['Observation', 1],
  ['Needs verification', 0],
]);

const canonicalActivityUrl = (item) => safeEvidenceUrl(item).replace(/\/$/, '').toLowerCase();

const activityIdentity = (item) => {
  const url = canonicalActivityUrl(item);
  if (url) return `url:${url}`;
  const id = normalizedText(firstText(
    item?.external_id,
    item?.intel_id,
    item?.evidence_id,
    item?.update_key,
    item?.id
  ));
  if (id) return `id:${id}`;
  return `content:${normalizedText(evidencePublisher(item))}|${normalizedText(evidenceTitle(item, ''))}`;
};

const activityPublisher = (fact) => {
  const references = asList(fact?.evidence);
  return firstText(
    fact?.source_name,
    fact?.publisher_name,
    fact?.origin_name,
    references.length ? evidencePublisher(references[0]) : ''
  );
};

const mergeActivity = (existing, incoming) => {
  const existingRank = ACTIVITY_STATUS_RANK.get(existing.status) ?? -1;
  const incomingRank = ACTIVITY_STATUS_RANK.get(incoming.status) ?? -1;
  const incomingTime = Date.parse(incoming.timestamp) || 0;
  const existingTime = Date.parse(existing.timestamp) || 0;
  return {
    ...existing,
    publisher: existing.publisher || incoming.publisher,
    title: existing.title || incoming.title,
    url: existing.url || incoming.url,
    timestamp: incomingTime > existingTime ? incoming.timestamp : existing.timestamp,
    status: incomingRank > existingRank ? incoming.status : existing.status,
    sourceType: existing.sourceType || incoming.sourceType,
  };
};

// Facts, source reports and merged observations are one operator-facing stream.
// The source URL is authoritative for de-duplication: if the same report appears
// as a verified fact, evidence row and baseline update, it is rendered once.
export const collectEvidenceActivity = (record) => {
  const rows = [];
  const push = (row) => {
    if (!row || (!row.title && !row.publisher && !row.url)) return;
    rows.push(row);
  };

  collectEvidence(record).forEach((item, index) => {
    push({
      key: activityIdentity(item) || `evidence:${index}`,
      publisher: evidencePublisher(item),
      title: evidenceTitle(item),
      url: safeEvidenceUrl(item),
      timestamp: evidenceTimestamp(item),
      status: evidenceActivityStatus(item),
      sourceType: evidenceSourceType(item),
      sortIndex: index,
    });
  });

  asList(record?.facts).forEach((fact, index) => {
    const references = asList(fact?.evidence);
    const reference = references.find((entry) => safeEvidenceUrl(entry)) || references[0] || {};
    const fallbackTitle = humanizeFact(fact);
    const factLikeSource = {
      ...reference,
      external_id: reference?.external_id || fact?.external_id,
      source_url: safeEvidenceUrl(reference) || safeEvidenceUrl(fact),
      source_name: evidencePublisher(reference) === 'Unknown publisher'
        ? activityPublisher(fact)
        : evidencePublisher(reference),
      title: evidenceTitle(reference, '') || fallbackTitle,
    };
    push({
      key: activityIdentity(factLikeSource) || `fact:${index}`,
      publisher: activityPublisher(fact) || evidencePublisher(factLikeSource),
      title: evidenceTitle(factLikeSource, fallbackTitle) || fallbackTitle,
      url: safeEvidenceUrl(factLikeSource),
      timestamp: evidenceTimestamp(fact) || evidenceTimestamp(reference),
      status: factActivityStatus(fact),
      sourceType: evidenceSourceType(reference),
      sortIndex: 1000 + index,
      publisherMatchAllowed: !safeEvidenceUrl(factLikeSource),
    });
  });

  collectLatestUpdates(record).forEach((item, index) => {
    push({
      key: activityIdentity(item) || `update:${index}`,
      publisher: evidencePublisher(item),
      title: evidenceTitle(item, 'Source update'),
      url: safeEvidenceUrl(item),
      timestamp: evidenceTimestamp(item),
      status: 'Update',
      sourceType: evidenceSourceType(item),
      sortIndex: 2000 + index,
    });
  });

  const byKey = new Map();
  rows.forEach((row) => {
    const current = byKey.get(row.key);
    byKey.set(row.key, current ? mergeActivity(current, row) : row);
  });

  // Older facts sometimes identify only their publisher. Merge them into the
  // sole linked report from that publisher instead of creating a technical,
  // linkless duplicate.
  const values = [...byKey.values()];
  const linkedByPublisher = new Map();
  values.forEach((row) => {
    if (!row.url) return;
    const publisher = normalizedText(row.publisher);
    if (!publisher) return;
    const matches = linkedByPublisher.get(publisher) || [];
    matches.push(row);
    linkedByPublisher.set(publisher, matches);
  });
  const merged = [];
  values.forEach((row) => {
    const candidates = row.publisherMatchAllowed
      ? linkedByPublisher.get(normalizedText(row.publisher)) || []
      : [];
    if (!row.url && candidates.length === 1) {
      const target = candidates[0];
      const targetIndex = merged.findIndex((entry) => entry.key === target.key);
      const combined = mergeActivity(target, row);
      if (targetIndex >= 0) merged[targetIndex] = combined;
      else {
        Object.assign(target, combined);
      }
      return;
    }
    if (!merged.some((entry) => entry.key === row.key)) merged.push(row);
  });

  return merged
    .sort((left, right) => {
      const leftTime = Date.parse(left.timestamp) || 0;
      const rightTime = Date.parse(right.timestamp) || 0;
      if (leftTime !== rightTime) return rightTime - leftTime;
      return left.sortIndex - right.sortIndex;
    })
    .map(({ publisherMatchAllowed, sortIndex, ...row }) => row);
};

export const evidenceItemKey = (item, index = 0) =>
  `${firstText(item?.external_id, item?.intel_id, item?.evidence_id, item?.id) || 'evidence'}|` +
  `${safeEvidenceUrl(item) || evidencePublisher(item)}|${index}`;
